// Une liste de publications — fil, profil, groupe, page, recherche.
//
// Elle remplace `ListePosts.jsx`, qui n'était qu'un conteneur prêté à l'ancienne
// machinerie. Les cartes sont maintenant de vrais composants, et tout ce que
// l'ancienne liste faisait à la main disparaît : plus de carte
// `postId -> élément`, plus de replacement de nœuds pour respecter l'ordre, plus
// de mise à jour ciblée pour ne pas refermer les commentaires ouverts. React
// fait cela à partir de la clé.
//
// Ce qui RESTE impératif, et c'est volontaire :
//   · `creerDefilement` — le chargement au défilement, avec sa sentinelle ;
//   · l'`IntersectionObserver` des vues — savoir ce qui est passé sous les yeux
//     du membre est une question de mise en page, pas d'état.

import { useEffect, useRef, useState } from "react";
import Store from "../store.js";
import { creerDefilement } from "../defilement.js";
import Publication from "./Publication.jsx";
import Vide from "./Vide.jsx";

const VIDE_PAR_DEFAUT = ["🕊️", "Rien à afficher pour l'instant", "Soyez le premier à partager quelque chose."];

/**
 * Marque comme vue une publication réellement passée sous les yeux du membre.
 *
 * On exige la moitié de la carte à l'écran : survoler le bas d'une page ne
 * compte pas. Une seule écriture par membre et par publication — le pilote
 * s'arrête tout seul si la vue existe déjà.
 */
function useObservateurVus(ctx) {
  const observateur = useRef(null);

  useEffect(() => {
    if (!ctx.signaux || !("IntersectionObserver" in window)) return;
    const obs = new IntersectionObserver(
      (entrees) => {
        for (const e of entrees) {
          if (!e.isIntersecting) continue;
          const id = e.target.dataset.id;
          ctx.signaux.marquerVu(id);
          /* ⚠️ PAS DE VUE SUR SES PROPRES PUBLICATIONS — 20 octobre 2026,
             trouvé à l'audit : faire défiler son propre fil comptait « 1 vue »
             que personne d'autre n'avait posée. Le classement, lui, doit bien
             savoir qu'on l'a vue — `marquerVu` reste. */
          if (e.target.dataset.auteur !== ctx.moi?.uid)
            Store.vues?.marquer("posts", id).catch(() => {});
          obs.unobserve(e.target);
        }
      },
      { threshold: 0.5 }
    );
    observateur.current = obs;
    return () => {
      obs.disconnect();
      observateur.current = null;
    };
  }, [ctx]);

  return observateur;
}

/**
 * Les squelettes d'attente.
 *
 * Avant, pendant le chargement, la liste ne rendait RIEN : un vide, puis tout
 * qui apparaît d'un coup. C'est ce qui donnait cette impression de saut — on
 * ouvrait le fil et il se remplissait brutalement, sans transition.
 *
 * Trois formes grises à la place. Elles ne mentent pas — elles ne prétendent
 * pas être du contenu — mais elles disent où les choses vont arriver, et
 * l'arrivée cesse d'être une surprise. C'est ce que fait Facebook.
 *
 * Trois, pas dix : le nombre de cartes qui tiennent sur un écran de téléphone.
 * Au-delà, on remplit une page qui ne sera jamais vue.
 */
function Squelettes() {
  return [0, 1, 2].map((i) => (
    <div className="squelette" key={i} aria-hidden="true">
      <div className="squelette__tete">
        <span className="squelette__rond" />
        <span className="squelette__lignes">
          <i style={{ width: "42%" }} />
          <i style={{ width: "26%" }} />
        </span>
      </div>
      <i className="squelette__ligne" style={{ width: "92%" }} />
      <i className="squelette__ligne" style={{ width: "76%" }} />
      <span className="squelette__image" />
    </div>
  ));
}

/** Le rendu commun : les cartes, ou l'état vide. */
function Cartes({ posts, ctx, raisons, observateurVus, vide }) {
  if (!posts.length) {
    const [symbole, titre, texte] = vide || VIDE_PAR_DEFAUT;
    return <Vide symbole={symbole} titre={titre} texte={texte} />;
  }
  return posts.map((p) => (
    <Publication
      key={p.id}
      p={p}
      ctx={ctx}
      raisons={raisons?.get(p.id)}
      observateurVus={observateurVus}
    />
  ));
}

/**
 * La liste qui va chercher ses pages elle-même.
 *
 * @param {Function} p.chargerPage       ({avant}) => Promise<publications>
 * @param {Function} [p.abonnerRecentes] (cb) => arrêt — la première page, en direct
 * @param {Function} [p.transformer]     (tout) => { posts, raisons? } — le classement
 * @param {*} [p.rafraichirQuand]        change → on redemande un classement
 * @param {Array} [p.deps]               de quoi dépend la liste
 */
export default function ListePublications({
  ctx,
  chargerPage,
  abonnerRecentes,
  transformer,
  rafraichirQuand,
  vide,
  deps = [],
}) {
  const [etat, setEtat] = useState({ posts: [], raisons: null, charge: false });
  const observateurVus = useObservateurVus(ctx);
  const hote = useRef(null);
  const defilement = useRef(null);

  // `transformer` change d'identité à chaque rendu du parent ; on le lit par
  // référence pour ne pas reconstruire le défilement à chaque fois.
  const transformerRef = useRef(transformer);
  transformerRef.current = transformer;

  useEffect(() => {
    const defil = creerDefilement({
      conteneur: hote.current,
      chargerPage,
      afficher: (tout) => {
        const r = transformerRef.current ? transformerRef.current(tout) : { posts: tout };
        setEtat({ posts: r.posts, raisons: r.raisons || null, charge: true });
      },
    });
    defilement.current = defil;
    const stop = abonnerRecentes?.((ps) => defil.majRecentes(ps));
    return () => {
      stop && stop();
      defil.detruire();
      defilement.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Le voisinage arrive après coup : le classement s'affine alors tout seul.
  const premier = useRef(true);
  useEffect(() => {
    if (premier.current) {
      premier.current = false;
      return;
    }
    defilement.current?.rafraichir();
  }, [rafraichirQuand]);

  return (
    // La sentinelle du défilement est posée JUSTE APRÈS ce conteneur, par
    // `creerDefilement`. Le parent n'a donc qu'un seul enfant React : rien que
    // React insère ensuite ne peut la déplacer.
    <div>
      <div className="liste-posts" ref={hote}>
        {etat.charge ? (
          <Cartes
            posts={etat.posts}
            ctx={ctx}
            raisons={etat.raisons}
            observateurVus={observateurVus}
            vide={vide}
          />
        ) : (
          <Squelettes />
        )}
      </div>
    </div>
  );
}

/** La même liste, mais dont on fournit soi-même les publications. */
export function ListePublicationsFournie({ ctx, posts, vide }) {
  const observateurVus = useObservateurVus(ctx);
  return (
    <div className="liste-posts">
      <Cartes posts={posts || []} ctx={ctx} observateurVus={observateurVus} vide={vide} />
    </div>
  );
}

/** Raccourci : les publications d'un membre. */
export function postsDeMembre(uid) {
  return {
    chargerPage: (o) => Store.posts.page({ ...o, auteurId: uid }),
    abonnerRecentes: (cb) => Store.posts.subscribeByAuthor(uid, cb),
  };
}
